import { autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import {
  defaultHighlightStyle,
  syntaxHighlighting,
  bracketMatching,
  foldGutter,
  foldKeymap
} from '@codemirror/language';
import { lintGutter, lintKeymap } from '@codemirror/lint';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { Extension } from '@codemirror/state';
import {
  keymap,
  highlightSpecialChars,
  drawSelection,
  highlightActiveLine,
  lineNumbers,
  highlightActiveLineGutter
} from '@codemirror/view';
import logger from '@goldcast/logger';

import {
  allHtmlTags,
  headStructure,
  bodyStructure,
  formSubmissionStructure,
  addToCalendarStructure,
  registerCtaStructure,
  customCtaStructure,
  customEventFunctionNames,
  METHOD_SEPARATOR
} from './marketingTags.constants';
import { baseThemeExtension } from './marketingTags.customExtensions';
import { CodeAreaConfig, CodeAreaKeys, DocStructure } from './store/types';

export const basicSetup: Extension[] = (() => [
  baseThemeExtension('300px'),
  lineNumbers(),
  highlightActiveLineGutter(),
  highlightSpecialChars(),
  history(),
  foldGutter(),
  drawSelection(),
  syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
  bracketMatching(),
  closeBrackets(),
  autocompletion(),
  highlightActiveLine(),
  highlightSelectionMatches(),
  keymap.of([
    ...closeBracketsKeymap,
    ...defaultKeymap,
    ...searchKeymap,
    ...historyKeymap,
    ...foldKeymap,
    ...completionKeymap,
    ...lintKeymap,
    indentWithTab
  ]),
  lintGutter()
])();

export function getRestrictedFromAllowedTags(allowedTags: string[]): string[] {
  return allHtmlTags.filter(tag => !allowedTags.includes(tag));
}

export function generateDoc(docStructure: DocStructure): string {
  return docStructure.firstLine + '\n' + docStructure.content + '\n' + docStructure.lastLine;
}

export function extractContent(doc: string): string {
  const lines = doc.split('\n');
  if (lines.length < 3) {
    return '';
  }
  const content = lines.slice(1, lines.length - 1).join('\n');
  return content;
}

export function deconstructCustomEventClass(classDoc: string): Record<CodeAreaKeys, string> {
  const customEventScripts = {} as Record<CodeAreaKeys, string>;
  if (!classDoc) {
    return customEventScripts;
  }
  const methods = extractContent(classDoc).split(`\n${METHOD_SEPARATOR}\n`);
  for (const method of methods) {
    const methodName = method.split('(')[0].trim();
    const methodContent = extractContent(method);
    const codeAreaKey = Object.keys(customEventFunctionNames).find(key => customEventFunctionNames[key] === methodName);
    if (codeAreaKey === undefined) {
      logger.error('Could not find code area key for method: ' + methodName);
      continue;
    }
    customEventScripts[codeAreaKey] = methodContent;
  }
  return customEventScripts;
}

export function getInitialTagStates(): Record<CodeAreaKeys, CodeAreaConfig> {
  return {
    [CodeAreaKeys.head]: {
      title: 'Head',
      description:
        'Code entered here will be placed in the HEAD of your registration and hub pages. Remember to wrap all Javascript in script tags',
      docStructure: { ...headStructure },
      language: 'html',
      readOnlyRanges: [
        {
          fromLine: 1,
          toLine: 1
        },
        {
          fromLine: 'last',
          toLine: 'last'
        }
      ],
      restrictedTags: getRestrictedFromAllowedTags([
        'script',
        'meta',
        'link',
        'style',
        'noscript',
        'title',
        'head',
        'base'
      ]),
      characterLimit: 4096
    },
    [CodeAreaKeys.body]: {
      title: 'Body',
      description: `Code entered here will be placed in the BODY of your registration and hub pages. Remember to wrap all Javascript in script tags`,
      docStructure: { ...bodyStructure },
      language: 'html',
      readOnlyRanges: [
        {
          fromLine: 1,
          toLine: 1
        },
        {
          fromLine: 'last',
          toLine: 'last'
        }
      ],
      restrictedTags: ['style', 'link', 'meta', 'title', 'head', 'base'],
      characterLimit: 4096
    },
    [CodeAreaKeys.formSubmission]: {
      title: 'Form Submission',
      description: `This function fires when a page visitor successfully submits a form. This custom event can be used to track registration conversions. Note: This function is not applicable on Hub Pages`,
      parametersDescription: `
      <ul>
        <li><i>submissionData</i> - An object containing the data submitted on the form. Access particular data points using a form field's property name e.g. submissionData.email and submissionData.first_name</li>
        <li><i>isReRegistration</i> - True or false (Boolean), indicating if the form submission is a re-registration. If false the form submission was a first time registration</li>
      </ul>`,
      isCustomEvent: true,
      docStructure: { ...formSubmissionStructure },
      language: 'javascript',
      readOnlyRanges: [
        {
          fromLine: 1,
          toLine: 1
        },
        {
          fromLine: 'last',
          toLine: 'last'
        }
      ]
    },
    [CodeAreaKeys.addToCalendar]: {
      title: 'Add to Calendar',
      description: `This function fires when a page visitor adds a session from the agenda to their calendar. Note: This function is not applicable on Hub Pages`,
      parametersDescription: `
      <ul>
        <li><i>sessionName</i> - The name of the agenda session added to the calendar</li>
        <li><i>sessionId</i> - The unique session ID of the session added to the calendar </li>
        <li><i>calendarType</i> - The calendar service selected by the visitor. Possible values are 'appleical', 'google', 'outlookcom', and 'outlook'</li>
      </ul>`,
      isCustomEvent: true,
      docStructure: { ...addToCalendarStructure },
      language: 'javascript',
      readOnlyRanges: [
        {
          fromLine: 1,
          toLine: 1
        },
        {
          fromLine: 'last',
          toLine: 'last'
        }
      ]
    },
    [CodeAreaKeys.registerCtaClick]: {
      title: 'Register CTA Click',
      description: `This function fires when a visitor clicks a register CTA. On a registration page a register CTA opens or scrolls to the form. On a Hub Page a register CTA redirects the visitor to an event registration page`,
      parametersDescription: `
      <ul>
        <li><i>eventId</i> - The unique ID of the event that the visitor is being directed to register for</li>
        <li><i>eventName</i> - The name of the event that the visitor is being directed to register for</li>
        <li><i>blockType</i> - The page block type that the register CTA was clicked from </li>
        <li><i>blockId</i> - The unique block ID that the register button was clicked from. Helpful for differentiating between multiple of the same block types on a page</li>
        <li><i>pageType</i> - The page type that the CTA was clicked on. Possible values are 'Registration' and 'Hub'</li>
      </ul>`,
      isCustomEvent: true,
      docStructure: { ...registerCtaStructure },
      language: 'javascript',
      readOnlyRanges: [
        {
          fromLine: 1,
          toLine: 1
        },
        {
          fromLine: 'last',
          toLine: 'last'
        }
      ]
    },
    [CodeAreaKeys.customCtaClick]: {
      title: 'Custom CTA Click',
      description: `This function fires when a visitor clicks a custom CTA. A custom CTA redirects the visitor to a custom URL set on the CTA`,
      parametersDescription: `
      <ul>
        <li><i>ctaText</i> - The text on the CTA button that the visitor clicked</li>
        <li><i>ctaUrl</i> - The URL that the CTA button navigates visitors to</li>
        <li><i>blockType</i> - The page block type that the register button was clicked from </li>
        <li><i>blockId</i> - The unique block ID that the register button was clicked from. Helpful for differentiating between multiple of the same block types on a page</li>
        <li><i>pageType</i> - The page type that the CTA was clicked on. Possible values are 'Registration' and 'Hub'</li>
      </ul>`,
      isCustomEvent: true,
      docStructure: { ...customCtaStructure },
      language: 'javascript',
      readOnlyRanges: [
        {
          fromLine: 1,
          toLine: 1
        },
        {
          fromLine: 'last',
          toLine: 'last'
        }
      ]
    }
  };
}
